/**
 * Accessibility Utilities for GuapGoals
 * 
 * Provides:
 * - Dark/Light mode detection and toggle
 * - Reduce Motion preference
 * - High Contrast mode
 * - Screen reader announcements
 * - Focus management
 */

// ==================== ACCESSIBILITY PREFERENCES ====================

const STORAGE_KEYS = {
  REDUCE_MOTION: 'guapgoals-reduce-motion',
  HIGH_CONTRAST: 'guapgoals-high-contrast',
  FONT_SIZE: 'guapgoals-font-size',
};

/**
 * Check if user prefers reduced motion (system preference)
 */
export function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Get reduce motion setting (user override or system preference)
 */
export function getReduceMotion() {
  const saved = localStorage.getItem(STORAGE_KEYS.REDUCE_MOTION);
  if (saved !== null) {
    return saved === 'true';
  }
  return prefersReducedMotion();
}

/**
 * Set reduce motion preference
 */
export function setReduceMotion(enabled) {
  localStorage.setItem(STORAGE_KEYS.REDUCE_MOTION, String(enabled));
  applyReduceMotion(enabled);
}

/**
 * Apply reduce motion to document
 */
export function applyReduceMotion(enabled) {
  const root = document.documentElement;
  if (enabled) {
    root.setAttribute('data-reduce-motion', 'true');
    root.style.setProperty('--animation-duration', '0.01ms');
    root.style.setProperty('--transition-duration', '0.01ms');
  } else {
    root.removeAttribute('data-reduce-motion');
    root.style.removeProperty('--animation-duration');
    root.style.removeProperty('--transition-duration');
  }
}

/**
 * Get high contrast setting
 */
export function getHighContrast() {
  return localStorage.getItem(STORAGE_KEYS.HIGH_CONTRAST) === 'true';
}

/**
 * Set high contrast preference
 */
export function setHighContrast(enabled) {
  localStorage.setItem(STORAGE_KEYS.HIGH_CONTRAST, String(enabled));
  applyHighContrast(enabled);
}

/**
 * Apply high contrast mode to document
 */
export function applyHighContrast(enabled) {
  const root = document.documentElement;
  if (enabled) {
    root.setAttribute('data-high-contrast', 'true');
  } else {
    root.removeAttribute('data-high-contrast');
  }
}

/**
 * Get font size preference (percentage)
 */
export function getFontSize() {
  const saved = localStorage.getItem(STORAGE_KEYS.FONT_SIZE);
  return saved ? parseInt(saved, 10) : 100;
}

/**
 * Set font size preference
 */
export function setFontSize(percentage) {
  localStorage.setItem(STORAGE_KEYS.FONT_SIZE, String(percentage));
  applyFontSize(percentage);
}

/**
 * Apply font size to document
 */
export function applyFontSize(percentage) {
  document.documentElement.style.fontSize = `${percentage}%`;
}

// ==================== INITIALIZATION ====================

/**
 * Initialize all accessibility preferences on app load
 */
export function initializeAccessibility() {
  // Apply saved preferences
  applyReduceMotion(getReduceMotion());
  applyHighContrast(getHighContrast());
  applyFontSize(getFontSize());
  
  // Listen for system preference changes
  if (typeof window !== 'undefined') {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    motionQuery.addEventListener('change', (e) => {
      // Only update if user hasn't set a preference
      if (localStorage.getItem(STORAGE_KEYS.REDUCE_MOTION) === null) {
        applyReduceMotion(e.matches);
      }
    });
  }
}

// ==================== SCREEN READER ANNOUNCEMENTS ====================

let announcer = null;

/**
 * Create the live region element for announcements
 */
function ensureAnnouncer() {
  if (announcer) return announcer;
  
  announcer = document.createElement('div');
  announcer.setAttribute('role', 'status');
  announcer.setAttribute('aria-live', 'polite');
  announcer.setAttribute('aria-atomic', 'true');
  announcer.className = 'sr-only';
  announcer.style.cssText = `
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `;
  document.body.appendChild(announcer);
  return announcer;
}

/**
 * Announce a message to screen readers
 * @param {string} message - The message to announce
 * @param {string} priority - 'polite' or 'assertive'
 */
export function announce(message, priority = 'polite') {
  const el = ensureAnnouncer();
  el.setAttribute('aria-live', priority);
  
  // Clear and set message to trigger announcement
  el.textContent = '';
  setTimeout(() => {
    el.textContent = message;
  }, 100);
}

// ==================== FOCUS MANAGEMENT ====================

/**
 * Trap focus within an element (for modals)
 */
export function trapFocus(element) {
  const focusableSelectors = [
    'button:not([disabled])',
    'a[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  
  const focusableElements = element.querySelectorAll(focusableSelectors);
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  
  function handleKeyDown(e) {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }
  
  element.addEventListener('keydown', handleKeyDown);
  firstElement?.focus();
  
  // Return cleanup function
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
  };
}

/**
 * Move focus to an element with announcement
 */
export function focusElement(element, announcement) {
  if (!element) return;
  
  element.focus();
  if (announcement) {
    announce(announcement);
  }
}

// ==================== KEYBOARD NAVIGATION HELPERS ====================

/**
 * Handle arrow key navigation for lists/grids
 */
export function handleArrowNavigation(e, items, currentIndex, onSelect) {
  let newIndex = currentIndex;
  
  switch (e.key) {
    case 'ArrowUp':
    case 'ArrowLeft':
      e.preventDefault();
      newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
      break;
    case 'ArrowDown':
    case 'ArrowRight':
      e.preventDefault();
      newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
      break;
    case 'Home':
      e.preventDefault();
      newIndex = 0;
      break;
    case 'End':
      e.preventDefault();
      newIndex = items.length - 1;
      break;
    default:
      return;
  }
  
  if (newIndex !== currentIndex) {
    onSelect(newIndex);
  }
}

// ==================== SKIP LINK ====================

/**
 * Create skip link HTML (should be first element in body)
 */
export function createSkipLink(targetId = 'main-content') {
  return `
    <a 
      href="#${targetId}" 
      class="skip-link"
      style="
        position: absolute;
        top: -40px;
        left: 0;
        background: var(--color-primary, #10b981);
        color: white;
        padding: 8px 16px;
        z-index: 10000;
        transition: top 0.3s;
        text-decoration: none;
        font-weight: bold;
      "
      onfocus="this.style.top='0'"
      onblur="this.style.top='-40px'"
    >
      Skip to main content
    </a>
  `;
}

export default {
  getReduceMotion,
  setReduceMotion,
  getHighContrast,
  setHighContrast,
  getFontSize,
  setFontSize,
  initializeAccessibility,
  announce,
  trapFocus,
  focusElement,
  handleArrowNavigation,
};
